import type { PiletApi } from '@chess/platform-web';
import { LOG_API, type Log } from '@chess/api-log';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { STORE_API, type GameStore } from '@chess/api-model';
import { REVIEW_API, type Review, type ReviewState } from '@chess/api-review';
import { REVIEWED_API } from '@chess/api-reviewed';
import { ReviewedRecord } from './record.ts';

// The `reviewed` API's provider (2026-10-19; the user: "reviewed means that
// the user accessed the game review screen at least once for the given
// game"): a game is reviewed once the review has shown it, i.e. the review
// API's state was `ready` with its id. The record is `reviewed/reviewed.json`
// (`record.ts`); on the first load, with no file yet, every game the store
// holds then counts as reviewed, so a user who already has games does not
// see a dot on all of them; games imported later start unreviewed. Home's
// game strip (shared-game-bar) reads it. This file only wires.
export function setup(app: PiletApi) {
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const failed = (what: string) => (error: unknown) => void log.then((l) => l.error(what, error instanceof Error ? error : { error: String(error) }));

  const record = new ReviewedRecord(
    app.getApi<Storage>(STORAGE_API).then((s) => s.scope('reviewed')),
    {
      firstIds: async () => {
        const store = await app.getApi<GameStore>(STORE_API);
        await store.ready();
        const ids = store.getState().games.map((g) => g.summary.id);
        void log.then((l) => l.info('first load: the games held count as reviewed', { count: ids.length }));
        return ids;
      },
      onError: (what, error) => failed(what)(error),
    },
  );
  app.registerApi(REVIEWED_API, record);
  record.ready().catch(failed('the record of reviewed games could not be read'));

  // Whatever game the review shows, the active board tab's, counts as reviewed.
  const follow = (state: ReviewState) => {
    if (state.status === 'ready' && state.gameId) record.mark(state.gameId);
  };
  void app
    .getApi<Review>(REVIEW_API)
    .then((review) => {
      follow(review.state());
      review.subscribe(follow);
    })
    .catch(failed('the review is not followed'));
}
